import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import { useEditorStore } from './index';
import useFinalizationDataStore from './useFinalizationDataStore';
import { disposePreviewObject, purgeElongationPreviews } from '../lib/elongationPreview';

const useElongationStore = create(
  devtools((set, get) => ({
    // Simplified Elongation Tool - only essential fields
    enabled: true,
    value: 0.0,
    unit: 'mm',
    isElongationApplied: false,
    previewMesh: null,
    isAppliedFromDraft: false,

    // Actions
    setElongation: (data) => set({ ...get(), ...data }),
    setPreviewMesh: (mesh) => {
      set({ previewMesh: mesh });
    },

    currentElongationCommand: null,
    setCurrentElongationCommand: (command) => set({ currentElongationCommand: command }),

    createElongationCommand: () => {
      const { value, unit, previewMesh } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
      const originalMaterial = sceneRefs.material;

      if (!previewMesh || !(parseFloat(value || 0) > 0)) return null;

      const originalMesh = sceneRefs.shadedMesh;
      const originalGeometry = originalMesh.geometry;
      const originalMatrix = originalMesh.matrix.clone();
      const originalElongation = { value, unit };
      console.log('🔍 DEBUG: Creating elongation command with originalElongation:', originalElongation);

      // Clone geometry and matrix from preview (finalized result)
      const cloneGeom = new THREE.BufferGeometry().copy(previewMesh.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(previewMesh.matrix);

      return {
        execute: () => {
          // Remove the preview visual. Sweeping the scene by name as well as
          // dropping the tracked clone: the handler re-creates the preview on
          // every shaded-mesh change, so a stale clone the store no longer
          // points at can still be sitting in the scene, and it would stay
          // there (blue, on top of the applied socket) after the swap below.
          disposePreviewObject(get().previewMesh);
          purgeElongationPreviews(sceneRefs.scene);

          const newMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          newMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          newMesh.matrix.decompose(newMesh.position, newMesh.quaternion, newMesh.scale);
          newMesh.matrixAutoUpdate = true;
          replaceShadedMesh(newMesh);

          // The clone captured when the command was built may differ from the
          // one the store held at execute time (mesh changes swap it out).
          disposePreviewObject(previewMesh);
          purgeElongationPreviews(useEditorStore.getState().sceneRefs?.scene);

          // Save to finalization store
          useFinalizationDataStore.getState().saveElongation(
            originalElongation.value,
            originalElongation.unit
          );

          console.log('🔍 DEBUG: Executing elongation command');
          set({
            enabled: true,
            value: originalElongation.value,
            unit: originalElongation.unit,
            isElongationApplied: true,
            previewMesh: null
          });
          const afterState = get();
          console.log('🔍 DEBUG: After execute, elongation store state:', afterState);
        },
        undo: () => {
          useFinalizationDataStore.getState().clearElongation();
          const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
          restoredMesh.matrix.copy(originalMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          set({
            enabled: true,
            value: originalElongation.value,
            unit: originalElongation.unit,
            isElongationApplied: false
          });
        }
      };
    },

    // Reset all data
    reset: () => {
      console.log('🔍 DEBUG: Resetting elongation store');
      set({
        enabled: true,
        value: 8.0,
        unit: 'mm',
        isElongationApplied: false,
        previewMesh: null
      });
    }
  }))
);

export default useElongationStore;